import type { GpsPermissionState } from "./scan-contract";

// Geolocation adapter for capture. A null / denied / unavailable location is NOT a capture blocker:
// the server does "flag, don't reject" GPS handling (ADR-004), and `permission` tells it WHY lat/lng
// are null. So captureLocation always RESOLVES (never rejects) with a permission-tagged result.

export interface GuardLocation {
  lat: number | null;
  lng: number | null;
  accuracyMeters: number | null;
  permission: GpsPermissionState;
}

/** The slice of the W3C Geolocation API we use — injectable for tests. */
export interface GeolocationLike {
  getCurrentPosition(
    success: (position: {
      coords: { latitude: number; longitude: number; accuracy: number };
    }) => void,
    error: (err: { code: number }) => void,
    options?: { enableHighAccuracy?: boolean; timeout?: number; maximumAge?: number },
  ): void;
}

const DEFAULT_TIMEOUT_MS = 10000;
const POSITION_PERMISSION_DENIED = 1; // GeolocationPositionError.PERMISSION_DENIED

/** Capture the current location, resolving (never rejecting) with a permission-tagged result. */
export function captureLocation(opts?: {
  geolocation?: GeolocationLike | undefined;
  timeoutMs?: number;
}): Promise<GuardLocation> {
  const geo = opts?.geolocation ?? resolveGeolocation();
  if (!geo) {
    return Promise.resolve(noFix("unavailable"));
  }
  const timeoutMs = opts?.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  return new Promise<GuardLocation>((resolve) => {
    let settled = false;
    const done = (location: GuardLocation): void => {
      if (settled) {
        return;
      }
      settled = true;
      resolve(location);
    };
    geo.getCurrentPosition(
      (position) => {
        const accuracy = position.coords.accuracy;
        done({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracyMeters: typeof accuracy === "number" && Number.isFinite(accuracy) ? accuracy : null,
          permission: "granted",
        });
      },
      (err) => {
        done(noFix(err.code === POSITION_PERMISSION_DENIED ? "denied" : "unavailable"));
      },
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 0 },
    );
  });
}

function resolveGeolocation(): GeolocationLike | undefined {
  if (typeof navigator !== "undefined" && navigator.geolocation) {
    return navigator.geolocation as unknown as GeolocationLike;
  }
  return undefined;
}

function noFix(permission: GpsPermissionState): GuardLocation {
  return { lat: null, lng: null, accuracyMeters: null, permission };
}
